/**
 * Regional Meal Times
 *
 * Centralized regional meal time windows and lookup functions.
 * Used by itineraryValidation.js and itineraryNormalizer.js.
 */

// ============================================
// REGIONAL MEAL TIME WINDOWS
// ============================================

export const REGIONAL_MEAL_TIMES = {
  // Format: { breakfast: [startHour, endHour], lunch: [start, end], dinner: [start, end] }

  // Default (US, most of Asia)
  default: {
    breakfast: [6, 10],
    lunch: [11, 14],
    dinner: [18, 21]
  },

  // UK - includes afternoon tea tradition
  uk: {
    breakfast: [7, 10],
    lunch: [12, 14],
    afternoon_tea: [15, 17], // Important UK tradition - not the same as dinner
    dinner: [18, 21]
  },

  // Spain, Portugal
  iberia: {
    breakfast: [7, 10],
    lunch: [14, 16],
    dinner: [21, 23]
  },

  // Argentina, Chile, Uruguay
  southern_cone: {
    breakfast: [7, 10],
    lunch: [13, 15],
    dinner: [21, 23]
  },

  // Italy, Greece
  mediterranean: {
    breakfast: [7, 10],
    lunch: [13, 15],
    dinner: [20, 22]
  },

  // France, Belgium
  france: {
    breakfast: [7, 10],
    lunch: [12, 14],
    dinner: [19, 21]
  },

  // Germany, Austria, Switzerland
  dach: {
    breakfast: [6, 9],
    lunch: [12, 14],
    dinner: [18, 20]
  },

  // Scandinavia
  nordic: {
    breakfast: [7, 9],
    lunch: [11, 13],
    dinner: [17, 19]
  },

  // Japan
  japan: {
    breakfast: [7, 9],
    lunch: [12, 13],
    dinner: [18, 20]
  },

  // Middle East (non-Ramadan)
  middle_east: {
    breakfast: [7, 10],
    lunch: [13, 15],
    dinner: [20, 22]
  },

  // India
  india: {
    breakfast: [7, 10],
    lunch: [12, 14],
    dinner: [19, 21]
  },

  // Brazil
  brazil: {
    breakfast: [7, 10],
    lunch: [12, 14],
    dinner: [20, 22]
  },

  // Mexico, Central America
  mexico: {
    breakfast: [7, 10],
    lunch: [14, 16],
    dinner: [20, 22]
  },

  // Southeast Asia
  southeast_asia: {
    breakfast: [6, 9],
    lunch: [11, 14],
    dinner: [18, 21]
  },
};

// ============================================
// COUNTRY → REGION MAPPING
// ============================================

export const COUNTRY_TO_MEAL_REGION = {
  // UK
  'united kingdom': 'uk', 'uk': 'uk', 'england': 'uk', 'scotland': 'uk', 'wales': 'uk', 'northern ireland': 'uk',
  'ireland': 'uk', // Similar tradition

  // Iberia
  'spain': 'iberia', 'portugal': 'iberia', 'andorra': 'iberia',

  // Southern Cone
  'argentina': 'southern_cone', 'chile': 'southern_cone', 'uruguay': 'southern_cone', 'paraguay': 'southern_cone',

  // Mediterranean
  'italy': 'mediterranean', 'greece': 'mediterranean', 'cyprus': 'mediterranean', 'malta': 'mediterranean',

  // France
  'france': 'france', 'belgium': 'france', 'monaco': 'france', 'luxembourg': 'france',

  // DACH
  'germany': 'dach', 'austria': 'dach', 'switzerland': 'dach', 'liechtenstein': 'dach',

  // Nordic
  'sweden': 'nordic', 'norway': 'nordic', 'denmark': 'nordic', 'finland': 'nordic', 'iceland': 'nordic',

  // Japan
  'japan': 'japan',

  // Middle East
  'uae': 'middle_east', 'saudi arabia': 'middle_east', 'qatar': 'middle_east', 'bahrain': 'middle_east',
  'oman': 'middle_east', 'kuwait': 'middle_east', 'jordan': 'middle_east', 'lebanon': 'middle_east',
  'israel': 'middle_east', 'egypt': 'middle_east', 'morocco': 'middle_east', 'tunisia': 'middle_east',

  // India
  'india': 'india', 'nepal': 'india', 'sri lanka': 'india', 'bangladesh': 'india',

  // Brazil
  'brazil': 'brazil',

  // Mexico & Central America
  'mexico': 'mexico', 'guatemala': 'mexico', 'honduras': 'mexico', 'el salvador': 'mexico',
  'nicaragua': 'mexico', 'costa rica': 'mexico', 'panama': 'mexico',

  // Southeast Asia
  'thailand': 'southeast_asia', 'vietnam': 'southeast_asia', 'indonesia': 'southeast_asia',
  'malaysia': 'southeast_asia', 'philippines': 'southeast_asia', 'cambodia': 'southeast_asia',
  'laos': 'southeast_asia', 'myanmar': 'southeast_asia', 'singapore': 'southeast_asia', 'brunei': 'southeast_asia',

  // ── B3 fix (May 2026): coverage extension for non-popular countries ──
  // Bhutan PDF showed dinner scheduled at 11:15 PM because Bhutan wasn't in
  // this map → fell through to `default` (breakfast 6-10, lunch 11-14,
  // dinner 18-21) which apparently allowed late dinners. Bhutan dinner is
  // 18:30-21:30. Extending coverage prevents the same fallthrough for
  // similar destinations.
  'bhutan': 'india',  // similar to South Asian mealtime conventions
  'mongolia': 'india',
  'pakistan': 'india',
  'maldives': 'india',
  'afghanistan': 'india',

  // South America (long tail)
  'bolivia': 'southern_cone', 'peru': 'southern_cone', 'ecuador': 'southern_cone',
  'colombia': 'southern_cone', 'venezuela': 'southern_cone', 'guyana': 'southern_cone',

  // Africa (covered partially via Middle East; rest fall through to default)
  'south africa': 'middle_east', 'kenya': 'middle_east', 'tanzania': 'middle_east',
  'ethiopia': 'middle_east', 'rwanda': 'middle_east', 'botswana': 'middle_east',
  'namibia': 'middle_east', 'zimbabwe': 'middle_east', 'madagascar': 'middle_east',
  'mauritius': 'middle_east', 'seychelles': 'middle_east',
};

// ============================================
// CITY → REGION MAPPING
// ============================================

export const CITY_TO_MEAL_REGION = {
  'barcelona': 'iberia', 'madrid': 'iberia', 'lisbon': 'iberia', 'seville': 'iberia',
  'buenos aires': 'southern_cone', 'santiago': 'southern_cone', 'montevideo': 'southern_cone',
  'rome': 'mediterranean', 'milan': 'mediterranean', 'florence': 'mediterranean', 'athens': 'mediterranean',
  'paris': 'france', 'lyon': 'france', 'nice': 'france', 'brussels': 'france',
  'berlin': 'dach', 'munich': 'dach', 'vienna': 'dach', 'zurich': 'dach',
  'stockholm': 'nordic', 'copenhagen': 'nordic', 'oslo': 'nordic', 'helsinki': 'nordic',
  'tokyo': 'japan', 'kyoto': 'japan', 'osaka': 'japan',
  'dubai': 'middle_east', 'abu dhabi': 'middle_east', 'doha': 'middle_east', 'tel aviv': 'middle_east',
  'mumbai': 'india', 'delhi': 'india', 'bangalore': 'india', 'goa': 'india',
  'rio de janeiro': 'brazil', 'sao paulo': 'brazil',
  'mexico city': 'mexico', 'cancun': 'mexico', 'playa del carmen': 'mexico',
  'bangkok': 'southeast_asia', 'chiang mai': 'southeast_asia', 'bali': 'southeast_asia',
  'singapore': 'southeast_asia', 'kuala lumpur': 'southeast_asia', 'hanoi': 'southeast_asia',
};

// ============================================
// LOOKUP FUNCTION
// ============================================

/**
 * Get regional meal times for a destination (sync version).
 * Uses static country and city maps for instant response.
 *
 * @param {string} destination - Destination name (city, country, or combined)
 * @returns {Object} Meal time windows { breakfast: [start, end], lunch: [start, end], dinner: [start, end] }
 */
export const getRegionalMealTimes = (destination) => {
  if (!destination) return REGIONAL_MEAL_TIMES.default;

  const lower = destination.toLowerCase();

  // Check country mapping
  for (const [country, region] of Object.entries(COUNTRY_TO_MEAL_REGION)) {
    if (lower.includes(country)) {
      return REGIONAL_MEAL_TIMES[region];
    }
  }

  // Check city-specific overrides
  for (const [city, region] of Object.entries(CITY_TO_MEAL_REGION)) {
    if (lower.includes(city)) {
      return REGIONAL_MEAL_TIMES[region];
    }
  }

  return REGIONAL_MEAL_TIMES.default;
};
